/**
 * The files on a message the operator is READING - one chip each, downloadable
 * where there is something to download.
 *
 * Shared by the thread view and by the pane that reads mail straight off the
 * IMAP server, because a file is a file: the two differ only in where the bytes
 * come from, which is the one thing the caller supplies.
 *
 * A chip with no bytes behind it is drawn as text rather than hidden. Messages
 * that arrived before the mailbox kept attachments still say what was on them,
 * and an operator who can see "leping.pdf, 240 kB" knows what to ask the client
 * for. Hiding it would turn a recoverable gap into a message that appears never
 * to have carried anything.
 *
 * Downloads are plain links, not fetches. The browser's own machinery handles
 * the save dialog, the progress and the retry, and the session cookie travels
 * with the request because the API is same-origin here (see `lib/api.ts`).
 */
import { Download, Paperclip } from "lucide-react";
import { cn } from "@wa/ui";
import { fmtBytes } from "../../../ui/format";
import type { useT } from "../../../../i18n/I18nProvider";

type T = ReturnType<typeof useT>;

/**
 * What a chip needs to know about a file.
 *
 * Structural rather than an import of the mail module's own type: a ticket
 * message carries the same three facts and the same one bit, and a component
 * shared by two sections must not make one of them depend on the other.
 */
export interface ChipAttachment {
  name: string;
  size: number;
  /**
   * Set when the bytes are actually held. Absent on a record written before
   * files were kept, and on one whose write to storage failed - both mean the
   * paperclip is a record, not a download. Optional rather than required so a
   * caller can pass its own richer type straight in.
   */
  stored?: boolean;
}

const CHIP =
  "group flex max-w-full items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs";

export function AttachmentChips({
  t,
  items,
  /** Where each file is fetched from, or null when it is only a record. */
  href,
  className,
}: {
  t: T;
  items: ChipAttachment[];
  href: (index: number, item: ChipAttachment) => string | null;
  className?: string;
}) {
  if (items.length === 0) return null;

  return (
    <div className={cn("mt-2 flex flex-wrap gap-2", className)}>
      {items.map((a, i) => {
        const url = href(i, a);
        const label = `${a.name} · ${fmtBytes(a.size)}`;

        if (!url) {
          return (
            <span
              key={i}
              className={cn(CHIP, "text-muted-foreground")}
              title={t("email.attachmentNotStored", { name: a.name })}
            >
              <Paperclip className="size-3 flex-none" />
              <span className="crm-trunc min-w-0">{label}</span>
            </span>
          );
        }

        return (
          <a
            key={i}
            href={url}
            // `download` asks for a save rather than a navigation. The server
            // sends `Content-Disposition: attachment` too, which is what
            // actually decides it - this attribute is the hint that keeps the
            // browser from opening a new tab first.
            download={a.name}
            className={cn(CHIP, "text-foreground hover:border-primary/40 hover:bg-primary/5")}
            title={t("email.attachmentDownload", { name: a.name })}
          >
            {/* The paperclip becomes a download arrow on hover, which on a
                touch screen means it never becomes one - so where there is no
                hover the chip simply says what it does from the start. */}
            <Paperclip className="size-3 flex-none text-muted-foreground group-hover:hidden [@media(hover:none)]:hidden" />
            <Download className="hidden size-3 flex-none text-primary group-hover:block [@media(hover:none)]:block" />
            <span className="crm-trunc min-w-0">{label}</span>
          </a>
        );
      })}
    </div>
  );
}
